import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography
} from '@mui/material';
import MainLayout from './MainLayout';

// Small date helpers
function toInputDate(date) {
  return date.toISOString().split('T')[0];
}
function fromInputDate(value) {
  return new Date(`${value}T00:00:00`);
}
function formatShort(dateString) {
  try {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  } catch { return '—'; }
}

function isWithin(dateString, startDate, endDate) {
  if (!dateString) return false;
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return false;
  return d >= startDate && d <= endDate;
}

function safeTime(value) {
  const t = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
}

function buildStatusText({ contract, startDate, endDate, performance, waiting, working, completed, goals }) {
  const startStr = startDate.toLocaleDateString('en-US');
  const endStr = endDate.toLocaleDateString('en-US');

  const balance = performance?.points_balance_including_working ?? performance?.points_balance ?? 0;
  const headerLines = [
    `Subject: Weekly Status – ${contract?.name || 'Contract'} (${contract?.number || ''}) – ${startStr} – ${endStr}`,
    `Points: Purchased ${performance?.points_purchased ?? 0} | Credited ${performance?.points_credited ?? 0} | Delivered ${performance?.points_delivered ?? 0} | Working ${performance?.points_working ?? 0} | Balance ${balance}`
  ];

  const waitingLines = [
    ``,
    `Waiting on Client (${waiting.length})`
  ].concat(
    waiting.map(t => `- ${t.name} • Due ${formatShort(t.due_date)}${t.link_to_review_tool ? ` • Review: ${t.link_to_review_tool}` : ''}`)
  );

  const workingLines = [
    ``,
    `What We're Working On (${working.length})`
  ].concat(
    working.map(t => `- ${t.name} • Due ${formatShort(t.due_date)} • ${(t.points ?? 0)} pts`)
  );

  const completedLines = [
    ``,
    `Completed (${completed.length})`
  ].concat(
    completed.map(t => `- ${t.name} • Completed ${formatShort(t.date_done)} • ${(t.points ?? 0)} pts`)
  );

  const goalLines = [
    ``,
    `Goals${goals.length ? '' : ' (none)'}`
  ].concat(
    goals.map(g => `- ${g.goal_name || g.goalName} • Due ${formatShort(g.due_date)} • ${g.percent_complete ?? 0}%`)
  );

  return [
    ...headerLines,
    ...waitingLines,
    ...workingLines,
    ...completedLines,
    ...goalLines
  ].join('\n');
}

const StatusPage = () => {
  const apiBaseUrl = process.env.REACT_APP_API_URL || '';

  // Defaults: last 14 days window
  const defaultEnd = new Date();
  const defaultStart = new Date();
  defaultStart.setDate(defaultEnd.getDate() - 14);

  const [contracts, setContracts] = useState([]); // normalized: { id, name, number }
  const [selectedContractId, setSelectedContractId] = useState('');
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [performance, setPerformance] = useState(null);
  const [waitingTasks, setWaitingTasks] = useState([]);
  const [workingTasks, setWorkingTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [goals, setGoals] = useState([]);

  const [statusText, setStatusText] = useState('');

  // Load contracts for selector (admins/team members see all)
  useEffect(() => {
    const loadContracts = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/contracts`);
        const raw = await res.json();
        const normalized = Array.isArray(raw) ? raw.map(c => ({
          id: c.contract_id || c.contractId || c.id || '',
          name: c.contract_name || c.contractName || c.name || '',
          number: c.external_id || c.externalId || c.contract_number || c.contractNumber || ''
        })) : [];
        const sorted = normalized.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setContracts(sorted);
      } catch (e) {
        // Fail silently for now
      }
    };
    loadContracts();
  }, [apiBaseUrl]);

  const selectedContract = useMemo(() => contracts.find(c => c.id === selectedContractId) || null, [contracts, selectedContractId]);

  const handleGetStatus = async () => {
    if (!selectedContractId) {
      setError('Please select a contract');
      return;
    }
    setError(null);
    setLoading(true);

    try {
      const [perfRes, goalsRes, waitRes, upRes, compRes] = await Promise.all([
        fetch(`${apiBaseUrl}/api/client-dashboard/performance/${selectedContractId}`),
        fetch(`${apiBaseUrl}/api/client-dashboard/goals/${selectedContractId}`),
        fetch(`${apiBaseUrl}/api/client-dashboard/tasks/waiting/${selectedContractId}`),
        fetch(`${apiBaseUrl}/api/client-dashboard/tasks/coming-up/${selectedContractId}`),
        fetch(`${apiBaseUrl}/api/client-dashboard/tasks/completed/${selectedContractId}`)
      ]);

      const [perf, gls, waitingAll, upcomingAll, completedAll] = await Promise.all([
        perfRes.json(), goalsRes.json(), waitRes.json(), upRes.json(), compRes.json()
      ]);

      const withinDue = t => isWithin(t.due_date, startDate, endDate);
      const withinDone = t => isWithin(t.date_done, startDate, endDate);

      const waiting = (Array.isArray(waitingAll) ? waitingAll.filter(withinDue) : [])
        .sort((a, b) => safeTime(a.due_date) - safeTime(b.due_date));

      const working = (Array.isArray(upcomingAll) ? upcomingAll.filter(withinDue) : [])
        .sort((a, b) => safeTime(a.due_date) - safeTime(b.due_date));

      const completed = (Array.isArray(completedAll) ? completedAll.filter(withinDone) : [])
        .sort((a, b) => (safeTime(a.due_date) - safeTime(b.due_date)) || (safeTime(a.date_done) - safeTime(b.date_done)));

      const goalsSorted = (Array.isArray(gls) ? gls : [])
        .sort((a, b) => safeTime(a.due_date) - safeTime(b.due_date));

      setPerformance(perf || {});
      setGoals(goalsSorted);
      setWaitingTasks(waiting);
      setWorkingTasks(working);
      setCompletedTasks(completed);

      const text = buildStatusText({
        contract: selectedContract,
        startDate,
        endDate,
        performance: perf || {},
        waiting,
        working,
        completed,
        goals: goalsSorted
      });
      setStatusText(text);
    } catch (e) {
      setError('Failed to get status.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(statusText || '');
    } catch {}
  };

  const handleDownload = () => {
    const blob = new Blob([statusText || ''], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'status.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <MainLayout title="Status">
      <Box sx={{ p: 3 }}>
        <Card className="details-card" sx={{ mb: 3 }}>
          <CardHeader title="Status" subheader="Generate a copy-ready weekly status update" />
          <Divider />
          <CardContent>
            <Box sx={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" sx={{ color: '#666', mb: 0.5 }}>Contract</Typography>
                <select
                  value={selectedContractId}
                  onChange={(e) => setSelectedContractId(e.target.value)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    minWidth: 360,
                    backgroundColor: '#fff'
                  }}
                >
                  <option value="" disabled>Select a contract…</option>
                  {contracts.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.number ? ` (${c.number})` : ''}
                    </option>
                  ))}
                </select>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" sx={{ color: '#666', mb: 0.5 }}>Start Date</Typography>
                <input
                  type="date"
                  value={toInputDate(startDate)}
                  onChange={(e) => setStartDate(fromInputDate(e.target.value))}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    minWidth: 180,
                    backgroundColor: '#fff'
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" sx={{ color: '#666', mb: 0.5 }}>End Date</Typography>
                <input
                  type="date"
                  value={toInputDate(endDate)}
                  onChange={(e) => setEndDate(fromInputDate(e.target.value))}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    minWidth: 180,
                    backgroundColor: '#fff'
                  }}
                />
              </Box>

              <Button variant="contained" onClick={handleGetStatus} disabled={!selectedContractId || loading}>
                {loading ? 'Generating…' : 'Get Status'}
              </Button>
            </Box>

            {error && (
              <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
            )}
          </CardContent>
        </Card>

        <Card className="details-card">
          <CardHeader title="Status Text" action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={handleCopy} disabled={!statusText}>Copy</Button>
              <Button size="small" onClick={handleDownload} disabled={!statusText}>Download</Button>
            </Box>
          } />
          <Divider />
          <CardContent>
            <Box component="pre" sx={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              fontSize: 14,
              p: 2,
              background: '#fafafa',
              border: '1px solid #eee',
              borderRadius: 1,
              minHeight: 180
            }}>
              {statusText || 'Status text will appear here after you click "Get Status".'}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </MainLayout>
  );
};

export default StatusPage; 